import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Container,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Build as MaintenanceIcon,
  LocalGasStation as FuelIcon,
  Settings as OperationalIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth0 } from '@auth0/auth0-react';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import styles from '../../styles/Analytics.module.css';

/**
 * CostAnalysis Page
 *
 * Displays operational cost analytics:
 * - Total operational costs
 * - Maintenance costs breakdown
 * - Cost per flight hour
 * - Cost trends over time
 *
 * Access: Owner, Admin, Manager roles only
 */
function CostAnalysis() {
  const { currentOrganization, userRole } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30'); // days
  const [costs, setCosts] = useState({
    totalCosts: 0,
    maintenanceCosts: 0,
    operationalCosts: 0,
    costPerFlight: 0,
    costPerFlightHour: 0,
    maintenanceBreakdown: [],
    costTrends: [],
    droneMaintenanceCosts: []
  });

  // Check if user has permission to view analytics
  const canView = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // Fetch cost data
  useEffect(() => {
    if (!currentOrganization?.organizationId || !canView) return;

    const fetchCostData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        // Fetch maintenance logs and flight logs
        const [dronesRes, flightLogsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/flight-logs`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        const dronesData = await dronesRes.json();
        const flightLogsData = await flightLogsRes.json();
        const drones = dronesData.drones || dronesData || [];
        const flightLogs = flightLogsData.flightLogs || [];


        // Fetch maintenance logs for each drone
        const maintenancePromises = drones.map(drone =>
          fetch(`${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones/${drone.id}/maintenance`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
            .then(res => res.json())
            .then(data => {
              const logs = data.maintenanceLogs || data.logs || [];
              if (logs.length > 0) {
              }
              return {
                droneId: drone.id,
                droneName: drone.name || drone.model,
                logs
              };
            })
            .catch(err => {
              return { droneId: drone.id, droneName: drone.name || drone.model, logs: [] };
            })
        );

        const maintenanceData = await Promise.all(maintenancePromises);

        // Filter by time range
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - parseInt(timeRange));

        const recentFlightLogs = flightLogs.filter(log => {
          const logDate = new Date(log.flightDate);
          return logDate >= cutoffDate;
        });

        // Filter maintenance logs by time range
        const filteredMaintenanceData = maintenanceData.map(({ droneId, droneName, logs }) => ({
          droneId,
          droneName,
          logs: logs.filter(log => {
            const logDate = new Date(log.performedDate);
            return logDate >= cutoffDate;
          })
        }));

        // Calculate costs
        const costData = calculateCosts(filteredMaintenanceData, recentFlightLogs, parseInt(timeRange));
        setCosts(costData);
      } catch (err) {
        setError('Failed to load cost analysis. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCostData();
  }, [currentOrganization, timeRange, canView, getAccessTokenSilently]);

  // Calculate cost metrics
  const calculateCosts = (maintenanceData, flightLogs, days) => {
    // Calculate maintenance costs
    let totalMaintenanceCosts = 0;
    const maintenanceByType = {};
    const droneMaintenanceCosts = [];

    maintenanceData.forEach(({ droneId, droneName, logs }) => {
      let droneTotalCost = 0;


      logs.forEach(log => {
        const cost = parseFloat(log.cost || 0);
        totalMaintenanceCosts += cost;
        droneTotalCost += cost;

        const type = log.maintenanceType || 'Other';
        maintenanceByType[type] = (maintenanceByType[type] || 0) + cost;
      });

      if (droneTotalCost > 0) {
        droneMaintenanceCosts.push({
          droneId,
          droneName,
          cost: droneTotalCost
        });
      }
    });


    const maintenanceBreakdown = Object.entries(maintenanceByType)
      .map(([type, cost]) => ({ type, cost }))
      .sort((a, b) => b.cost - a.cost);

    // Calculate operational costs based on actual subscription active days
    const subscriptionCost = parseFloat(currentOrganization?.subscription?.amount) || 0;
    const interval = currentOrganization?.subscription?.interval || 'month';
    const daysInInterval = interval === 'year' ? 365 : 30;
    const dailySubscriptionCost = subscriptionCost / daysInInterval;

    // Calculate actual days the subscription was active during the time range
    const today = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const subscriptionStart = currentOrganization?.subscription?.createdAt
      ? new Date(currentOrganization.subscription.createdAt)
      : null;
    const subscriptionEnd = currentOrganization?.subscription?.endedAt
      ? new Date(currentOrganization.subscription.endedAt)
      : currentOrganization?.subscription?.canceledAt
      ? new Date(currentOrganization.subscription.canceledAt)
      : today;

    // Determine the overlap period between time range and subscription active period
    let activeDays = 0;
    if (subscriptionStart) {
      const rangeStart = cutoffDate;
      const rangeEnd = today;

      // Get the overlap start (later of range start or subscription start)
      const overlapStart = subscriptionStart > rangeStart ? subscriptionStart : rangeStart;
      // Get the overlap end (earlier of range end or subscription end)
      const overlapEnd = subscriptionEnd < rangeEnd ? subscriptionEnd : rangeEnd;

      // Calculate days only if there's actual overlap
      if (overlapStart <= overlapEnd) {
        activeDays = Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24));
      }
    }

    const operationalCosts = dailySubscriptionCost * activeDays;

    // Total costs
    const totalCosts = totalMaintenanceCosts + operationalCosts;

    // Cost per flight (based on actual flight logs)
    const flightCount = flightLogs.length;
    const costPerFlight = flightCount > 0 ? (totalCosts / flightCount).toFixed(2) : 0;

    // Cost per flight hour (based on actual flight logs)
    const totalHours = flightLogs.reduce((sum, log) => {
      return sum + parseFloat(log.flightDuration || 0);
    }, 0);
    const costPerFlightHour = totalHours > 0 ? (totalCosts / totalHours).toFixed(2) : 0;

    // Cost trends (simplified - by week)
    const costTrends = [];
    for (let i = 0; i < 4; i++) {
      const weekCost = totalCosts / 4; // Simplified equal distribution
      costTrends.push({
        week: `Week ${i + 1}`,
        cost: weekCost.toFixed(2)
      });
    }

    return {
      totalCosts: totalCosts.toFixed(2),
      maintenanceCosts: totalMaintenanceCosts.toFixed(2),
      operationalCosts: operationalCosts.toFixed(2),
      costPerFlight,
      costPerFlightHour,
      maintenanceBreakdown,
      costTrends,
      droneMaintenanceCosts: droneMaintenanceCosts.sort((a, b) => b.cost - a.cost)
    };
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!canView) {
    return (
      <OrganizationLayout>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            You do not have permission to view this page. Only Owners, Admins, and Managers can access analytics.
          </Alert>
        </Box>
      </OrganizationLayout>
    );
  }

  return (
    <OrganizationLayout>
      <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 2 : 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 2 : 0 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MoneyIcon sx={{ fontSize: isMobile ? 24 : 32, color: '#1976d2' }} />
              <Typography variant={isMobile ? "h5" : "h4"} component="h1">
                Cost Analysis
              </Typography>
            </Box>
            <Typography variant={isMobile ? "body2" : "body1"} color="textSecondary">
              Track operational costs and identify cost-saving opportunities.
            </Typography>
          </Box>

          {/* Time Range Filter */}
          <FormControl sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="7">Last 7 Days</MenuItem>
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
              {/* Total Costs */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <MoneyIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Total Costs
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.1rem' : '3rem' }}>
                      {formatCurrency(costs.totalCosts)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Last {timeRange} days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Maintenance Costs */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <MaintenanceIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Maintenance
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.1rem' : '3rem' }}>
                      {formatCurrency(costs.maintenanceCosts)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Maintenance costs
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Cost Per Flight */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <TrendingUpIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Per Flight
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.1rem' : '3rem' }}>
                      {formatCurrency(costs.costPerFlight)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Average cost/flight
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Cost Per Hour */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <OperationalIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Per Hour
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.1rem' : '3rem' }}>
                      {formatCurrency(costs.costPerFlightHour)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Average cost/hour
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Cost Breakdown */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: isMobile ? 1.5 : 3 }}>
              {/* Cost Categories */}
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                      Cost Categories
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Maintenance Costs</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(costs.maintenanceCosts)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: '#e0e0e0',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(costs.maintenanceCosts / costs.totalCosts) * 100}%`,
                            height: '100%',
                            bgcolor: '#ff9800'
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Operational Costs</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(costs.operationalCosts)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: '#e0e0e0',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(costs.operationalCosts / costs.totalCosts) * 100}%`,
                            height: '100%',
                            bgcolor: '#2196f3'
                          }}
                        />
                      </Box>
                    </Box>

                    <Alert severity="info" sx={{ mt: 3, fontSize: '0.85rem' }}>
                      Operational costs are calculated based on the actual days your subscription was active during the selected time period.
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              {/* Maintenance Breakdown */}
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                      Maintenance Breakdown
                    </Typography>
                    {costs.maintenanceBreakdown.length > 0 ? (
                      isMobile ? (
                        <Stack spacing={1}>
                          {costs.maintenanceBreakdown.map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                {item.type}
                              </Typography>
                              <Chip
                                label={formatCurrency(item.cost)}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {costs.maintenanceBreakdown.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{item.type}</TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={formatCurrency(item.cost)}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )
                    ) : (
                      <Typography variant="body2" color="textSecondary" align="center" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                        No maintenance costs recorded
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Drone Maintenance Costs */}
            <Card elevation={2}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                  Maintenance Costs by Drone
                </Typography>
                {costs.droneMaintenanceCosts.length > 0 ? (
                  isMobile ? (
                    <Stack spacing={1.5}>
                      {costs.droneMaintenanceCosts.map((drone, index) => (
                        <Card key={drone.droneId} variant="outlined">
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={`#${index + 1}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                  {drone.droneName}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#ff9800' }}>
                                {formatCurrency(drone.cost)}
                              </Typography>
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${(drone.cost / costs.maintenanceCosts) * 100}%`,
                                      height: '100%',
                                      bgcolor: '#ff9800',
                                      borderRadius: 1
                                    }}
                                  />
                                </Box>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                                  {((drone.cost / costs.maintenanceCosts) * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Drone</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Total Cost</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Percentage</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {costs.droneMaintenanceCosts.map((drone, index) => (
                            <TableRow key={drone.droneId}>
                              <TableCell>#{index + 1}</TableCell>
                              <TableCell>{drone.droneName}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(drone.cost)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: `${(drone.cost / costs.maintenanceCosts) * 150}px`,
                                      maxWidth: 150,
                                      height: 8,
                                      bgcolor: '#ff9800',
                                      borderRadius: 1
                                    }}
                                  />
                                  <Typography variant="caption">
                                    {((drone.cost / costs.maintenanceCosts) * 100).toFixed(1)}%
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    No maintenance costs recorded
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </OrganizationLayout>
  );
}

export default CostAnalysis;
